/**
 * @file themeService.js
 * @description This file contains the code for submitting a theme to the server and uploading files.
 */

import { clientApiHttp, contentHttp } from './httpService';

const getTenantId = () => +window.localStorage.getItem('tenantId');
const getCompanyCode = () => window.localStorage.getItem('companyCode');

/**
 * Submits a theme to the server.
 *
 * @param {Object} themeData - The theme data to be submitted.
 * @param {number} themeData.tenantId - The ID of the tenant.
 * @param {string} themeData.primaryLogo - The url for primary logo image (optional).
 * @param {string} themeData.secondaryLogo - The url for secondary logo image (optional).
 * @param {string} themeData.favicon - The url for favicon image (optional).
 * @param {string} themeData.name - The name of the theme.
 * @param {string} themeData.pageHeaderColor1 - The first page header color.
 * @param {string} themeData.pageHeaderColor2 - The second page header color (optional).
 * @param {string} themeData.accessoryColor - The accessory color.
 * @param {string} themeData.accessoryTextColor - The accessory text color.
 * @param {string} themeData.loginBackgroundColor - The login background color (optional).
 * @param {string} themeData.loginBackgroundImage - The url for login background image (optional).
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error submitting the theme.
 */
export const updateTheme = async themeData => {
    try {
        const tenantId = themeData?.tenantId || getTenantId();
        const response = await clientApiHttp.put(
            `/v2/client/${tenantId}/themes`,
            {
                ...themeData,
                tenantId,
            },
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to submit theme');
    }
};

/**
 * Creates a new theme for the tenant.
 *
 * @param {Object} themeData - The theme data to be created.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error creating the theme.
 */
export const createTheme = async themeData => {
    try {
        const tenantId = themeData?.tenantId || getTenantId();
        const response = await clientApiHttp.post(
            `/v2/client/${tenantId}/themes`,
            {
                ...themeData,
                tenantId,
            },
        );
        return response.data;
    } catch (error) {
        throw new Error('Failed to create theme');
    }
};

/**
 * Fetches the current theme settings applied to the application.
 *
 * @param {string} tenantId - The ID of the tenant.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error fetching the current theme settings.
 */
export const getCurrentTheme = async companyCode => {
    try {
        const params = new URLSearchParams();
        if (companyCode) {
            params.append('companyCode', companyCode);
        }

        const queryString = params.toString();
        const url = `/v2/client/themes/active-theme${queryString ? `?${queryString}` : ''}`;

        const response = await clientApiHttp.get(url);
        return response.data;
    } catch (error) {
        // throw new Error("Failed to fetch current theme");
        console.error('Error retrieving theme data:', error);
        return null;
    }
};

/**
 * Uploads a file to the server.
 *
 * @param {File} file - The file to be uploaded.
 * @returns {Promise<Object>} - The response data from the server.
 * @throws {Error} - If there is an error uploading the file.
 *
 */
export const uploadFile = async file => {
    try {
        const tenantID = getTenantId();
        const formData = new FormData();
        formData.append('file', file);
        const response = await contentHttp.post(
            `/v2/content/tenant/${tenantID}/containers/file`,
            formData,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
            },
        );

        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error('Error uploading file');
        }
    } catch (error) {
        throw new Error('Error uploading file');
    }
};
